import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost } from './client';

export type NotificationChannel = 'email' | 'slack' | 'webhook';
export type NotificationCategory = 'transactional' | 'clinical' | 'marketing' | 'operational';

export type RecipientRule =
  | { type: 'user'; path: string }
  | { type: 'slackChannel'; channel: string }
  | { type: 'email'; path: string }
  | { type: 'emailLiteral'; address: string }
  | { type: 'webhook'; url: string };

export interface SubscriptionDto {
  id: string;
  eventType: string;
  templateCode: string;
  recipientRule: RecipientRule;
  orgId: string | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSubscriptionInput {
  eventType: string;
  templateCode: string;
  recipientRule: RecipientRule;
  orgId?: string;
  enabled?: boolean;
}

export interface UpdateSubscriptionInput {
  templateCode?: string;
  recipientRule?: RecipientRule;
  enabled?: boolean;
}

export interface TemplateDto {
  id: string;
  code: string;
  channel: NotificationChannel;
  category: NotificationCategory;
  phi: boolean;
  subject: string | null;
  bodyTemplate: string;
  locale: string;
  version: number;
  source: 'seeded' | 'custom';
  orgId: string | null;
  createdAt: string;
  updatedAt: string;
}

export function buildQS(params?: Record<string, string | boolean | undefined>): string {
  if (!params) return '';
  const qs = new URLSearchParams(
    Object.entries(params)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, String(v)]),
  ).toString();
  return qs ? `?${qs}` : '';
}

const SUBSCRIPTIONS_KEY = ['subscriptions'] as const;
const TEMPLATES_KEY = ['notification-templates'] as const;

export function useSubscriptions(filters?: {
  eventType?: string;
  orgId?: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [...SUBSCRIPTIONS_KEY, filters ?? {}],
    queryFn: () => apiGet<SubscriptionDto[]>(`/api/notifications/subscriptions${buildQS(filters)}`),
  });
}

export function useTemplates(filters?: {
  channel?: NotificationChannel;
  category?: NotificationCategory;
}) {
  return useQuery({
    queryKey: [...TEMPLATES_KEY, filters ?? {}],
    queryFn: () => apiGet<TemplateDto[]>(`/api/notifications/templates${buildQS(filters)}`),
  });
}

export function useCreateSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSubscriptionInput) =>
      apiPost<SubscriptionDto>('/api/notifications/subscriptions', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: SUBSCRIPTIONS_KEY }),
  });
}

export function useUpdateSubscription(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateSubscriptionInput) =>
      apiPatch<SubscriptionDto>(`/api/notifications/subscriptions/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: SUBSCRIPTIONS_KEY }),
  });
}

export function useDeleteSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/api/notifications/subscriptions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: SUBSCRIPTIONS_KEY }),
  });
}
